import { create } from 'zustand';
// The registry must be populated before anything reads it; see batchStore for the same
// reason spelled out.
import '../techniques/register';
import { BatchEngine } from '../bulk/engine';
import type { BatchProgress, Variant, VariantMeta } from '../bulk/types';
import { breedPopulation, DEFAULT_EVOLVE_CONFIG, type EvolveConfig } from '../evolve/genetics';
import { createRng, randomSeed } from '../core/rng';
import { TEXT_TECHNIQUE_ID } from './layer';
import type { StudioDocument } from './studioStore';

/**
 * State for the evolution mode.
 *
 * Deliberately separate from the batch store even though both render populations of
 * documents: a batch is a *plan* — you configure it, run it, and sift the result — while
 * evolution is a conversation, where each round depends on what you liked in the last
 * one. Sharing one store would mean one of the two carrying fields it never uses.
 *
 * Rendering is the batch worker pool, unchanged. It already isolates failures and
 * replaces dead workers, and a generation is exactly the kind of small parallel job it
 * was built for.
 */
export interface EvolveState {
  config: EvolveConfig;
  /** The composition the first generation grew out of. */
  base: StudioDocument | null;
  generation: number;
  population: Variant[];
  /** Documents chosen in the generation just past; the parents of the next. */
  parents: StudioDocument[];
  progress: BatchProgress;
  /** History of how many were chosen per generation, for the lineage strip. */
  lineage: { generation: number; chosen: number }[];
  thumbSize: number;
  detailId: string | null;

  setConfig: (patch: Partial<EvolveConfig>) => void;
  setThumbSize: (n: number) => void;
  setDetail: (id: string | null) => void;

  /** Starts over from a studio document. */
  seedFrom: (doc: StudioDocument) => void;
  /** Breeds the next generation from whatever is currently chosen. */
  advance: () => void;
  toggleChosen: (id: string) => void;
  chooseNone: () => void;
  cancel: () => void;
  reset: () => void;
}

const idleProgress = (): BatchProgress => ({
  status: 'idle',
  total: 0,
  completed: 0,
  failed: 0,
  elapsed: 0,
  remaining: null,
  currentTechnique: '',
});

/** Preview size for a tile in the population grid, in px on the longest edge. */
const PREVIEW_SIZE = 380;

let engine: BatchEngine | null = null;

function describe(doc: StudioDocument, index: number, generation: number): VariantMeta {
  return {
    index,
    seed: doc.seed,
    word: String(doc.textValues.text ?? '').split('\n')[0] || '—',
    fontId: doc.fontId,
    techniques: doc.layers.filter((l) => l.techniqueId !== TEXT_TECHNIQUE_ID).map((l) => l.techniqueId),
    palette: '—',
    canvas: `${doc.canvas.width}×${doc.canvas.height}`,
    dimensions: { Generation: generation },
  };
}

export const useEvolve = create<EvolveState>((set, get) => {
  const ensureEngine = (): BatchEngine => {
    if (engine) return engine;
    engine = new BatchEngine({
      onVariant: (variant) =>
        set((s) => ({ population: s.population.map((v) => (v.id === variant.id ? { ...variant } : v)) })),
      onProgress: (progress) => set({ progress }),
      onFinished: (status) => set((s) => ({ progress: { ...s.progress, status } })),
    });
    return engine;
  };

  /** Renders a population and replaces the current one. */
  const render = (docs: StudioDocument[], generation: number): void => {
    for (const v of get().population) if (v.url) URL.revokeObjectURL(v.url);

    const population: Variant[] = docs.map((doc, i) => ({
      id: `g${generation}-${i}`,
      meta: describe(doc, i, generation),
      doc,
      blob: null,
      url: null,
      width: 0,
      height: 0,
      ms: 0,
      elements: 0,
      favourite: false,
      hidden: false,
      error: null,
    }));

    set({ population, generation, detailId: null });

    const workers = Math.max(2, Math.min(8, (navigator.hardwareConcurrency || 4) - 1));
    ensureEngine().start(
      population.map((variant) => ({
        variant,
        scale: Math.min(1, PREVIEW_SIZE / Math.max(variant.doc.canvas.width, variant.doc.canvas.height)),
        format: 'webp' as const,
        quality: 0.85,
        maxSteps: 40000,
      })),
      workers,
    );
  };

  return {
    config: DEFAULT_EVOLVE_CONFIG,
    base: null,
    generation: 0,
    population: [],
    parents: [],
    progress: idleProgress(),
    lineage: [],
    thumbSize: 200,
    detailId: null,

    setConfig: (patch) => set((s) => ({ config: { ...s.config, ...patch } })),
    setThumbSize: (thumbSize) => set({ thumbSize }),
    setDetail: (detailId) => set({ detailId }),

    seedFrom: (doc) => {
      const base = structuredClone(doc);
      const config = get().config;
      const docs = breedPopulation([], base, config, createRng(randomSeed()));
      set({ base, parents: [], lineage: [] });
      render(docs, 1);
    },

    advance: () => {
      const { population, base, config } = get();
      if (!base) return;
      const chosen = population.filter((v) => v.favourite && !v.error).map((v) => v.doc);
      const docs = breedPopulation(chosen, base, config, createRng(randomSeed()));
      set((s) => ({
        parents: chosen,
        lineage: [...s.lineage, { generation: s.generation, chosen: chosen.length }].slice(-40),
      }));
      render(docs, get().generation + 1);
    },

    toggleChosen: (id) =>
      set((s) => ({ population: s.population.map((v) => (v.id === id ? { ...v, favourite: !v.favourite } : v)) })),

    chooseNone: () => set((s) => ({ population: s.population.map((v) => ({ ...v, favourite: false })) })),

    cancel: () => engine?.cancel(),

    reset: () => {
      engine?.cancel();
      for (const v of get().population) if (v.url) URL.revokeObjectURL(v.url);
      set({
        base: null,
        generation: 0,
        population: [],
        parents: [],
        lineage: [],
        progress: idleProgress(),
        detailId: null,
      });
    },
  };
});
